/* eslint-disable react/prop-types */
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import buildRequestUrl from "lib/requests/requestUtils";
import athenaClient from "lib/axios/clients";
import { useEffectAsync } from "lib/react/customHooks";
import { makeStyles, Typography } from "@material-ui/core";
import { BsCircleFill } from "react-icons/bs";
import _ from "lodash";
import { normalizeBetweenTwoRanges, getQueryFilters } from "lib/utils";
import { resampleHistoricsToMonthly } from "lib/utils/historics";
import CardComponent from "app/common/CardComponent";
import moment from "moment";
import { setTupleData } from "../duck/slices";
import MarketShareChartComponent from "./MarketShareChartComponent";
import ExplicabilityComponent from "../../explicability-info/ExplicabilityComponent";

const useStyles = makeStyles((theme) => ({
  chartContainer: {
    position: "relative",
    overflow: "hidden",
    flexGrow: "1",
    [theme.breakpoints.up("xs")]: {
      height: "155px",
    },
    [theme.breakpoints.up("sm")]: {
      height: "200px",
    },
    [theme.breakpoints.up("md")]: {
      height: "300px",
    },
    [theme.breakpoints.up("lg")]: {
      height: "100%",
    },
  },
  componentTitle: {
    fontWeight: "bold",
    fontSize: "14px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "16px",
    },
  },
}));

function MarketShareComponent() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const filtersSlice = useSelector((state) => state.filters);
  const companiesSlice = useSelector((state) => state.companies);
  const competitorsSlice = useSelector((state) => state.competitors);
  const [marketShareData, setMarketShareData] = React.useState(null);
  const sidebarFilters = _.pick(filtersSlice.filters, [
    "category",
    "retailer",
    "area",
  ]);
  const sidebarQueryFilters = getQueryFilters(sidebarFilters);

  const getChartDataForDataset = (labels, dateRanges, dataset) => {
    return _.map(labels, (date) => {
      const historic = _.find(dataset, (record) => {
        return moment(record.date).format("MMM'YY") === date;
      });

      return historic
        ? {
            ...historic,
            label: companiesSlice[dataset[0].company_id].label,
            x: date,
            y: (100 * historic.sum_units) / dateRanges[historic.date].sum_units,
            sum_units: {
              absolute:
                (100 * historic.sum_units) /
                dateRanges[historic.date].sum_units,
            },
          }
        : {
            label: companiesSlice[dataset[0].company_id].label,
            x: date,
            y: 0,
            sum_units: {
              absolute: 0,
            },
          };
    });
  };

  const retrieveMarketShareForTuple = async (tupleKey) => {
    if (competitorsSlice.tuples[tupleKey]?.marketShare) {
      return {
        key: tupleKey,
        marketShare: competitorsSlice.tuples[tupleKey].marketShare,
      };
    }

    // In order to be able to retrieve the las 12 available months of market share history we first need to know the maximum available date
    const maxMarketShareDateRequest = await athenaClient({
      method: "GET",
      url: buildRequestUrl(
        {
          filters: {
            ...sidebarQueryFilters,
          },
          group_by: ["date"],
          order_by: ["date"],
          order_direction: ["desc"],
          limit: 1,
        },
        "historical_volume_by_company"
      ),
    });

    // If there is no data, we return
    if (maxMarketShareDateRequest.data.length === 0) {
      return {
        key: tupleKey,
        marketShare: null,
      };
    }

    const maxMarketShareDate = new Date(maxMarketShareDateRequest.data[0].date);

    const marketShareUrl = buildRequestUrl(
      {
        filters: {
          ...sidebarQueryFilters,
          min_date: new Date(
            maxMarketShareDate.getFullYear(),
            maxMarketShareDate.getMonth() - 11
          )
            .toISOString()
            .slice(0, 10),
          max_date: maxMarketShareDate.toISOString().slice(0, 10),
        },
        sum: ["units"],
        group_by: ["company_id", "date"],
      },
      "historical_volume_by_company"
    );

    const marketShareConfig = {
      method: "GET",
      url: marketShareUrl,
    };

    const marketShareResult = await athenaClient(marketShareConfig);

    // historical_volume_by_company can come back at daily/weekly cadence
    // instead of one row per month; resample per company so the rest of this
    // component keeps seeing ~1 row per month per company, as before.
    return {
      key: tupleKey,
      marketShare: resampleHistoricsToMonthly(marketShareResult.data, [
        "company_id",
      ]),
    };
  };

  useEffectAsync(
    async () => {
      const tupleKey = `marketshare-${filtersSlice.filtersKey}`;
      return retrieveMarketShareForTuple(tupleKey);
    },
    (result) => {
      const tupleData = {
        marketShare: result.marketShare,
      };

      dispatch(
        setTupleData({
          [result.key]: tupleData,
        })
      );

      const dateRanges = _(result.marketShare)
        .groupBy("date")
        .mapValues((a) => ({
          sum_units: _.sumBy(a, "sum_units"),
        }))
        .value();

      const marketShareDataByCompany = _.groupBy(
        result.marketShare,
        "company_id"
      );

      const differentDates = _.map(
        _.uniqBy(result.marketShare, "date"),
        (historic) => historic.date
      );

      const formattedDates = _.map(differentDates, (date) => {
        return moment(date).format("MMM'YY");
      });

      const [ownCompanyDataset, competitorCompaniesDatasets] = _.partition(
        marketShareDataByCompany,
        (companyData) => {
          return companiesSlice[companyData[0].company_id].is_client_company;
        }
      );

      let companyIndexCounter = 1;
      const marketShareChartData = {
        labels: formattedDates,
        datasets: [
          ...(ownCompanyDataset.length === 0
            ? []
            : [
                {
                  label: ownCompanyDataset[0][0].company_id,
                  yAxisID: "historical_volume_by_company",
                  type: "line",
                  fill: "origin",
                  borderColor: "rgba(51, 173, 174, 1)",
                  pointBordercolor: "rgba(51, 173, 174, 1)",
                  pointBackgroundColor: "#ffffff",
                  backgroundColor: "rgba(51, 173, 174, 0.5)",
                  hoverBackgroundColor: "rgba(51, 173, 174, 0.7)",
                  hoverBorderWidth: 3,
                  hoverBorderColor: "rgba(51, 173, 174, 0.7)",
                  pointHoverBackgroundColor: "#ffffff",
                  pointHoverRadius: 6,
                  hoverBorderDash: [4, 3],
                  data: getChartDataForDataset(
                    formattedDates,
                    dateRanges,
                    ownCompanyDataset[0]
                  ),
                },
              ]),
          ...(competitorCompaniesDatasets.length === 0
            ? []
            : _.map(competitorCompaniesDatasets, (companyDataHistory) => {
                const datasetAlpha =
                  1 -
                  normalizeBetweenTwoRanges(
                    companyIndexCounter,
                    2,
                    competitorCompaniesDatasets.length,
                    0.2,
                    0.8
                  );
                companyIndexCounter += 1;
                return {
                  label: companyDataHistory[0].company_id,
                  yAxisID: "historical_volume_by_company",
                  type: "line",
                  fill: "-1",
                  borderColor: `rgba(255, 200, 74, 1)`,
                  pointBordercolor: `rgba(255, 200, 74, 1)`,
                  pointBackgroundColor: "#ffffff",
                  backgroundColor: `rgba(255, 200, 74, ${datasetAlpha}`,
                  hoverBackgroundColor: `rgba(255, 200, 74, 0.9)`,
                  hoverBorderWidth: 3,
                  hoverBorderColor: `rgba(255, 200, 74, 0.9)`,
                  pointHoverBackgroundColor: "#ffffff",
                  pointHoverRadius: 6,
                  hoverBorderDash: [4, 3],
                  data: getChartDataForDataset(
                    formattedDates,
                    dateRanges,
                    companyDataHistory
                  ),
                };
              })),
        ],
      };

      setMarketShareData(marketShareChartData);
    },
    [filtersSlice.filters]
  );

  const explicabilityTooltipText = (
    <div style={{ whiteSpace: "pre-line" }}>
      <>
        Historical market share for your company<>&ensp;</>
        <BsCircleFill size="6px" color="#10A2B1" />
        {"\n"}
        and your competitors<>&ensp;</>
        <BsCircleFill size="6px" color="#FFC84A" />
      </>
    </div>
  );

  return (
    <>
      {marketShareData && (
        <CardComponent
          title={
            <Typography variant="body1" className={classes.componentTitle}>
              Market Share By Company
              <ExplicabilityComponent tooltipText={explicabilityTooltipText} />
            </Typography>
          }
          body={
            <div className={classes.chartContainer}>
              <MarketShareChartComponent data={marketShareData} />
            </div>
          }
        />
      )}
    </>
  );
}

export default MarketShareComponent;
